import { useState } from "react";
import { CheckCircle2, Circle, PackageSearch, XCircle } from "lucide-react";
import { inr, usePageTitle } from "@/context/ShopContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STEPS = ["received", "confirmed", "shipped", "delivered"];
const LABELS = {
  received: "Order Received",
  confirmed: "Confirmed",
  shipped: "Shipped",
  delivered: "Delivered",
};

export default function Track() {
  usePageTitle("Track Your Order");
  const [orderNumber, setOrderNumber] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [order, setOrder] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    setOrder(null);
    try {
      const r = await fetch(`${API}/orders/track`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ order_number: orderNumber, email }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Order not found");
      setOrder(data);
      setStatus("found");
    } catch (err) {
      setError(err.message);
      setStatus("error");
    }
  };

  const stepIndex = order ? STEPS.indexOf(order.status) : -1;
  const cancelled = order?.status === "cancelled";

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-16 md:py-24" data-testid="track-page">
      <p className="overline-label mb-4">Customer Care</p>
      <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tight">TRACK YOUR ORDER</h1>
      <p className="text-sm sm:text-base text-espresso/70 mt-4 max-w-xl">
        Enter your order number (from your confirmation email, e.g. LM-A1B2C3) and the
        email you used at checkout.
      </p>

      <form onSubmit={submit} className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl">
        <input
          required
          data-testid="track-order-number"
          className="input-lux uppercase"
          placeholder="Order number (LM-XXXXXX)"
          value={orderNumber}
          onChange={(e) => setOrderNumber(e.target.value)}
        />
        <input
          required
          type="email"
          data-testid="track-email"
          className="input-lux"
          placeholder="Email used at checkout"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <button type="submit" data-testid="track-submit" disabled={status === "loading"} className="btn-primary sm:col-span-2 sm:w-auto">
          {status === "loading" ? "Checking…" : "Track Order"}
        </button>
      </form>

      {status === "error" && (
        <div className="mt-10 border border-rose/30 bg-white p-6 flex gap-4 max-w-xl" data-testid="track-error">
          <PackageSearch className="w-5 h-5 text-rose shrink-0 mt-0.5" strokeWidth={1.5} />
          <p className="text-sm text-espresso/75">{error} Please double-check both fields — the email must match the one used when ordering.</p>
        </div>
      )}

      {order && (
        <div className="mt-12 bg-white border border-beige p-8 md:p-10" data-testid="track-result">
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <h2 className="font-serif text-2xl">Order {order.order_number}</h2>
            <p className="text-xs uppercase tracking-[0.2em] text-taupe">
              {order.payment_status === "paid" ? "Payment received" : order.payment_status === "pending_payment" ? "Awaiting payment" : order.payment_status === "cod_pending" ? "Cash on Delivery" : order.payment_status}
            </p>
          </div>

          {(order.shipping_partner || order.tracking_number) && (
            <div className="mt-6 border border-beige bg-ivory px-5 py-4 flex flex-wrap gap-x-10 gap-y-2" data-testid="track-shipping-info">
              {order.shipping_partner && (
                <div>
                  <p className="overline-label">Delivery Partner</p>
                  <p className="text-sm mt-1">{order.shipping_partner}</p>
                </div>
              )}
              {order.tracking_number && (
                <div>
                  <p className="overline-label">Tracking Number</p>
                  <p className="text-sm mt-1 font-medium tracking-wide">{order.tracking_number}</p>
                </div>
              )}
            </div>
          )}

          {cancelled ? (
            <div className="mt-8 flex items-center gap-3 text-rose" data-testid="track-cancelled">
              <XCircle className="w-5 h-5" strokeWidth={1.5} />
              <p className="text-sm">This order has been cancelled. If you have questions, contact us and we'll help right away.</p>
            </div>
          ) : (
            <ol className="mt-10 space-y-0" data-testid="track-timeline">
              {STEPS.map((s, i) => {
                const done = i <= stepIndex;
                return (
                  <li key={s} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      {done ? (
                        <CheckCircle2 className="w-6 h-6 text-espresso" strokeWidth={1.5} />
                      ) : (
                        <Circle className="w-6 h-6 text-beige" strokeWidth={1.5} />
                      )}
                      {i < STEPS.length - 1 && <span className={`w-px flex-1 min-h-[28px] ${i < stepIndex ? "bg-espresso" : "bg-beige"}`} />}
                    </div>
                    <div className="pb-8">
                      <p className={`text-sm uppercase tracking-[0.15em] ${done ? "text-espresso" : "text-taupe"}`}>{LABELS[s]}</p>
                      {s === "shipped" && order.shipped_at && done && (
                        <p className="text-xs text-taupe mt-1">Shipped on {new Date(order.shipped_at).toLocaleDateString("en-IN", { day: "numeric", month: "long" })}</p>
                      )}
                      {s === "received" && order.created_at && (
                        <p className="text-xs text-taupe mt-1">Placed on {new Date(order.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long" })}</p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}

          <div className="border-t border-beige pt-6 mt-2">
            <p className="text-xs uppercase tracking-[0.2em] text-taupe mb-4">Items</p>
            {order.items.map((it, i) => (
              <div key={`${it.name}-${i}`} className="flex justify-between gap-4 text-sm py-1.5">
                <span className="text-espresso/80">
                  {it.name}{it.shade && !it.selections?.length ? ` — ${it.shade}` : ""} × {it.qty}
                  {it.selections?.length > 0 && <span className="block text-xs text-taupe mt-0.5">{it.selections.join(", ")}</span>}
                </span>
              </div>
            ))}
            <div className="flex justify-between text-sm font-medium pt-4 mt-2 border-t border-beige">
              <span>Total {order.city ? `· to ${order.city}` : ""}</span>
              <span data-testid="track-total">{inr(order.total)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
